import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ProjectService, { Project } from '../services/ProjectService';
import ProjectModal from './ProjectModal';

interface ProjectsTabProps {
  partnerId: number;
}

export default function ProjectsTab({ partnerId }: ProjectsTabProps): React.JSX.Element {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    loadProjects();
  }, [partnerId]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const data = await ProjectService.listByPartner(partnerId);
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    setModalOpen(true);
  };

  const handleEdit = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProject(project);
    setModalOpen(true);
  };

  const handleView = (project: Project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleDelete = async (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Delete project "${project.name}"?`);
    if (!confirmed) return;

    try {
      await ProjectService.delete(project.id);
      toast.success('Project deleted');
      loadProjects();
    } catch (err: any) {
      console.error('Failed to delete project:', err);
      const message = err?.response?.data?.detail || 'Delete failed';
      toast.error(message);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(projects.map((p) => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (projectId: number, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(projectId);
    } else {
      newSet.delete(projectId);
    }
    setSelectedIds(newSet);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(`${selectedIds.size} Projekt(e) wirklich löschen?`);
    if (!confirmed) return;

    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of Array.from(selectedIds)) {
      try {
        await ProjectService.delete(id);
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to delete project ${id}:`, err);
      }
    }

    setBulkDeleting(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} Projekt(e) gelöscht`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} Projekt(e) konnten nicht gelöscht werden`);
    }

    loadProjects();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingProject(null);
  };

  const handleModalSave = () => {
    setModalOpen(false);
    setEditingProject(null);
    loadProjects();
  };

  const formatLocation = (project: Project): string => {
    const parts = [project.postal_code, project.city].filter(Boolean);
    return parts.join(' ') || '-';
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Projects ({projects.length})
        </h3>
        <button
          onClick={handleAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '6px',
            background: '#021a4a',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <FaPlus size={12} />
          Create project
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #fcd34d',
          }}
        >
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            {selectedIds.size} ausgewählt
          </span>
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: '#dc2626',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: bulkDeleting ? 'wait' : 'pointer',
              opacity: bulkDeleting ? 0.7 : 1,
            }}
          >
            <FaTrash size={12} />
            {bulkDeleting ? 'Lösche...' : 'Löschen'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={{
              background: 'none',
              border: 'none',
              color: '#92400e',
              fontSize: '13px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Auswahl aufheben
          </button>
        </div>
      )}

      {/* Table */}
      {projects.length === 0 ? (
        <div
          style={{
            padding: '60px',
            textAlign: 'center',
            color: '#888',
            background: '#f8f9fc',
            borderRadius: '8px',
          }}
        >
          <p style={{ margin: 0, fontSize: '16px' }}>No projects yet</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Click "Create project" to get started.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
              <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={projects.length > 0 && selectedIds.size === projects.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </th>
              <th style={thStyle}>Project</th>
              <th style={{ ...thStyle, width: '120px' }}>Status</th>
              <th style={thStyle}>Location</th>
              <th style={{ ...thStyle, width: '120px' }}>Created</th>
              <th style={{ ...thStyle, width: '120px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              const statusColor = ProjectService.getStatusColor(project.status);
              return (
                <tr
                  key={project.id}
                  style={{
                    borderBottom: '1px solid #e0e3ef',
                    cursor: 'pointer',
                    background: selectedIds.has(project.id) ? '#fefce8' : 'transparent',
                  }}
                  onClick={() => handleView(project)}
                >
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(project.id)}
                      onChange={(e) => handleSelectOne(project.id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#021a4a' }}>{project.name}</div>
                      {project.reference && (
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          Ref: {project.reference}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {ProjectService.getStatusLabel(project.status)}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatLocation(project)}</td>
                  <td style={tdStyle}>{formatDate(project.created_at)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(project);
                        }}
                        title="Details"
                        style={actionBtnStyle}
                      >
                        <FaEye size={14} />
                      </button>
                      <button
                        onClick={(e) => handleEdit(project, e)}
                        title="Edit"
                        style={actionBtnStyle}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(project, e)}
                        title="Delete"
                        style={{ ...actionBtnStyle, color: '#dc2626' }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Modals */}
      <ProjectModal
        isOpen={modalOpen}
        project={editingProject}
        partnerId={partnerId}
        onClose={handleModalClose}
        onSave={handleModalSave}
      />
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333',
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: '#666',
  borderRadius: '4px',
};
